/**
 * IPO Value Types — canonical v5.x / v4.4 vocabulary.
 *
 * Verified against INPA.exe's type-name table at `FUN_0046456b` and
 * its constants reader at `FUN_00463bd7`. See
 * `docs/ipo-format-versions.md` for the reverse-engineering notes.
 *
 * Older `Handle1` / `Handle2` / `Handle3` names (used for slots
 * `0x07` / `0x08` / `0x09` before 2026-05) were wrong — the actual
 * type names INPA uses internally are `ULONG`, `NUMERIC`, `OBJECT`.
 * They are not opaque handles.
 *
 * `Numeric` is special: it's a 4-byte numeric value the runtime can
 * coerce through ASCII-LONG conversion (see `TryConversion from both
 * ASCII-LONG` MessageBoxA path at `INPA.exe!FUN_0045ffdc`).
 *
 * v1.x scripts (NCSEXPERT) use a different, smaller type-byte
 * vocabulary that doesn't map cleanly onto this enum — those are
 * decoded via a version-conditional parser path. Don't reuse this
 * enum for v1.x type bytes.
 */
export var ValueType;
(function (ValueType) {
    ValueType[ValueType["Void"] = 0] = "Void";
    ValueType[ValueType["Bool"] = 1] = "Bool";
    ValueType[ValueType["Byte"] = 2] = "Byte";
    ValueType[ValueType["Int"] = 3] = "Int";
    ValueType[ValueType["Long"] = 4] = "Long";
    ValueType[ValueType["Real"] = 5] = "Real";
    ValueType[ValueType["String"] = 6] = "String";
    ValueType[ValueType["ULong"] = 7] = "ULong";
    ValueType[ValueType["Numeric"] = 8] = "Numeric";
    ValueType[ValueType["Object"] = 9] = "Object";
})(ValueType || (ValueType = {}));
/**
 * IPO Block Types
 */
export var BlockType;
(function (BlockType) {
    BlockType[BlockType["Screen"] = 1] = "Screen";
    BlockType[BlockType["Menu"] = 2] = "Menu";
    BlockType[BlockType["StateMachine"] = 3] = "StateMachine";
    BlockType[BlockType["LogicTable"] = 4] = "LogicTable";
    BlockType[BlockType["Function"] = 5] = "Function";
    BlockType[BlockType["GlobalData"] = 17] = "GlobalData";
    BlockType[BlockType["ConstantData"] = 18] = "ConstantData";
    BlockType[BlockType["ScreenFunc"] = 33] = "ScreenFunc";
    BlockType[BlockType["LineFunc"] = 34] = "LineFunc";
    BlockType[BlockType["ControlFunc"] = 35] = "ControlFunc";
    BlockType[BlockType["MenuItemFunc"] = 36] = "MenuItemFunc";
    BlockType[BlockType["StateFunc"] = 37] = "StateFunc";
})(BlockType || (BlockType = {}));
/**
 * VM Opcodes
 * Based on docs/opcode-reference.md and research/opcode-mappings.md
 */
export var Opcode;
(function (Opcode) {
    Opcode[Opcode["LOAD"] = 1] = "LOAD";
    Opcode[Opcode["PUSHREF"] = 2] = "PUSHREF";
    Opcode[Opcode["LOADINOUTREF"] = 3] = "LOADINOUTREF";
    Opcode[Opcode["NOP"] = 4] = "NOP";
    Opcode[Opcode["MOVE"] = 5] = "MOVE";
    Opcode[Opcode["PUSHR"] = 6] = "PUSHR";
    Opcode[Opcode["PUSHREFSTORE"] = 7] = "PUSHREFSTORE";
    Opcode[Opcode["ALLOC"] = 8] = "ALLOC";
    Opcode[Opcode["ALU"] = 9] = "ALU";
    Opcode[Opcode["JMP"] = 10] = "JMP";
    Opcode[Opcode["JMPZ"] = 11] = "JMPZ";
    Opcode[Opcode["CALL"] = 12] = "CALL";
    Opcode[Opcode["CALLE"] = 13] = "CALLE";
    Opcode[Opcode["RET"] = 14] = "RET";
    Opcode[Opcode["FRAME"] = 15] = "FRAME";
    Opcode[Opcode["LOGTABLE"] = 16] = "LOGTABLE";
    Opcode[Opcode["PUSHIMM"] = 17] = "PUSHIMM";
})(Opcode || (Opcode = {}));
/**
 * ALU Operations
 */
export var AluOp;
(function (AluOp) {
    AluOp[AluOp["ADD"] = 96] = "ADD";
    AluOp[AluOp["SUB"] = 97] = "SUB";
    AluOp[AluOp["MUL"] = 98] = "MUL";
    AluOp[AluOp["DIV"] = 99] = "DIV";
    AluOp[AluOp["LT"] = 100] = "LT";
    AluOp[AluOp["GT"] = 101] = "GT";
    AluOp[AluOp["LE"] = 102] = "LE";
    AluOp[AluOp["GE"] = 103] = "GE";
    AluOp[AluOp["EQ"] = 104] = "EQ";
    AluOp[AluOp["NE"] = 105] = "NE";
    AluOp[AluOp["AND"] = 106] = "AND";
    AluOp[AluOp["OR"] = 107] = "OR";
    AluOp[AluOp["XOR"] = 108] = "XOR";
    AluOp[AluOp["NEG"] = 109] = "NEG";
    AluOp[AluOp["NOT"] = 110] = "NOT";
    AluOp[AluOp["BAND"] = 111] = "BAND";
    AluOp[AluOp["BOR"] = 112] = "BOR";
    AluOp[AluOp["BXOR"] = 113] = "BXOR";
})(AluOp || (AluOp = {}));
/**
 * Variable Scope
 */
export var Scope;
(function (Scope) {
    Scope[Scope["Global"] = 0] = "Global";
    Scope[Scope["Const"] = 1] = "Const";
    Scope[Scope["Local"] = 2] = "Local";
    Scope[Scope["Screen"] = 64] = "Screen";
    Scope[Scope["Menu"] = 65] = "Menu";
    Scope[Scope["StateMachine"] = 66] = "StateMachine";
})(Scope || (Scope = {}));
/**
 * Call Target Type
 */
export var CallTarget;
(function (CallTarget) {
    CallTarget[CallTarget["UserFunction"] = 128] = "UserFunction";
    CallTarget[CallTarget["SystemFunction"] = 129] = "SystemFunction";
})(CallTarget || (CallTarget = {}));
/**
 * Type markers in bytecode (for ALLOC/PUSHIMM opcodes).
 *
 * Verified against INPA.exe's TypeMarker→ValueType mapper at
 * `FUN_00460f29`. Used by:
 *   - ALLOC (opcode 0x08): all eight markers initialize a local var
 *   - PUSHIMM (opcode 0x11): only 0x50–0x53 (the inline-byte forms)
 *
 * Note: there is no marker for `Numeric` (ValueType 8). NUMERIC values
 * arrive from constants/loads and are coerced to BOOL/BYTE/INT/LONG
 * via `FUN_0045ffdc` → `FUN_0046014a` at load time, before the ALU
 * dispatcher sees them. INPA.exe's CAST opcode (originally 0x04) is a
 * no-op — type coercion happens through these markers and the
 * load-time NUMERIC path, not via an explicit CAST opcode.
 */
export var TypeMarker;
(function (TypeMarker) {
    TypeMarker[TypeMarker["Bool"] = 80] = "Bool";
    TypeMarker[TypeMarker["Int"] = 81] = "Int";
    TypeMarker[TypeMarker["Byte"] = 82] = "Byte";
    TypeMarker[TypeMarker["Long"] = 83] = "Long";
    TypeMarker[TypeMarker["Real"] = 84] = "Real";
    TypeMarker[TypeMarker["String"] = 85] = "String";
    TypeMarker[TypeMarker["Object"] = 86] = "Object";
    TypeMarker[TypeMarker["ULong"] = 87] = "ULong";
})(TypeMarker || (TypeMarker = {}));
